import React, { useState, useEffect, useCallback } from 'react';
import { X, ThumbsUp } from 'lucide-react';

// Norwegian cities for realistic social proof
const NORWEGIAN_CITIES = [
  'Oslo', 'Bergen', 'Trondheim', 'Stavanger', 'Kristiansand',
  'Drammen', 'Fredrikstad', 'Tromsø', 'Sandnes', 'Sarpsborg',
  'Bodø', 'Ålesund', 'Haugesund', 'Tønsberg', 'Moss',
  'Sandefjord', 'Arendal', 'Hamar', 'Larvik', 'Halden',
  'Lillehammer', 'Molde', 'Gjøvik', 'Skien', 'Porsgrunn'
];

// Norwegian first names - more variety
const NORWEGIAN_NAMES = [
  // Female names
  'Maria', 'Ingrid', 'Kari', 'Anne', 'Hanne', 'Silje', 'Camilla', 'Ida',
  'Stine', 'Line', 'Marte', 'Emilie', 'Nora', 'Sara', 'Julie', 'Thea',
  'Emma', 'Sofie', 'Malin', 'Kristine', 'Elisabeth', 'Hege', 'Lene', 'Nina',
  // Male names
  'Lars', 'Erik', 'Anders', 'Jonas', 'Martin', 'Thomas', 'Kristian', 'Andreas',
  'Morten', 'Ole', 'Per', 'Bjørn', 'Ola', 'Magnus', 'Henrik', 'Fredrik',
  'Simen', 'Espen', 'Geir', 'Trond', 'Knut', 'Håkon', 'Petter', 'Svein'
];

// Products to show in social proof - varied selection with realistic weights
const PRODUCTS = [
  {
    name: "Lion's Mane Fokus 500 mg",
    shortName: "Lion's Mane Fokus",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/Sb7e922d9fbba42d99c52d119b4bbfd55L.webp?v=1767979674",
    weight: 28 // Most popular
  },
  {
    name: "Magnesium L-Threonate 2000 mg",
    shortName: "Magnesium L-Threonate",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/Scf2bfcd824cf4b3cbe26ab0885552ae8a.webp?v=1767979673",
    weight: 22
  },
  {
    name: "Omega-3 Fiskeolje 1200 mg",
    shortName: "Omega-3 Fiskeolje",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/Sd8502a2b30c84a05ae21ec7d9803576fv.webp?v=1767979673",
    weight: 20
  },
  {
    name: "Ashwagandha 1350 mg",
    shortName: "Ashwagandha",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/S75e7d92e1c2a44e4ac3a4f0e0c82f7a6W.webp?v=1767979673",
    weight: 15
  },
  {
    name: "Vitamin D3+K2 2000 IU",
    shortName: "Vitamin D3+K2",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/Sa7e2e2d8a9674dd2a59c2a3d5f8d6b49C.webp?v=1767979673",
    weight: 10
  },
  {
    name: "Kreatin Monohydrat 5000 mg",
    shortName: "Kreatin Monohydrat",
    image: "https://cdn.shopify.com/s/files/1/0791/3960/2656/files/S3a9d3e1d9b0446d0a37c1d6b5e8f9c2aP.webp?v=1767979673",
    weight: 5
  }
];

// Keep track of shown products to avoid immediate repetition
let recentlyShownProducts = [];
let recentlyShownNames = [];

// Weighted random selection with no immediate repeats
function getWeightedRandomProduct() {
  const availableProducts = PRODUCTS.filter(p => !recentlyShownProducts.includes(p.name));
  const productsToUse = availableProducts.length >= 2 ? availableProducts : PRODUCTS;
  
  const totalWeight = productsToUse.reduce((sum, p) => sum + p.weight, 0);
  let random = Math.random() * totalWeight;
  
  let selectedProduct = productsToUse[0];
  for (const product of productsToUse) {
    random -= product.weight;
    if (random <= 0) {
      selectedProduct = product;
      break;
    }
  }
  
  recentlyShownProducts.push(selectedProduct.name);
  if (recentlyShownProducts.length > 3) {
    recentlyShownProducts.shift();
  }
  
  return selectedProduct;
}

function getRandomCity() {
  return NORWEGIAN_CITIES[Math.floor(Math.random() * NORWEGIAN_CITIES.length)];
}

function getRandomName() {
  // Avoid showing same name twice in a row
  let name;
  do {
    name = NORWEGIAN_NAMES[Math.floor(Math.random() * NORWEGIAN_NAMES.length)];
  } while (recentlyShownNames.includes(name) && recentlyShownNames.length < NORWEGIAN_NAMES.length - 5);
  
  recentlyShownNames.push(name);
  if (recentlyShownNames.length > 4) {
    recentlyShownNames.shift();
  }
  
  return name;
}

// Realistic time calculation based on time of day
function getRealisticTimeAgo() {
  const now = new Date();
  const currentHour = now.getHours();
  
  // Different patterns based on time of day
  if (currentHour >= 7 && currentHour < 12) {
    // Morning: purchases from 15-45 min ago
    const minutes = Math.floor(Math.random() * 30) + 15;
    return `for ${minutes} min siden`;
  } else if (currentHour >= 12 && currentHour < 18) {
    // Afternoon: purchases from 10-60 min ago
    const minutes = Math.floor(Math.random() * 50) + 10;
    if (minutes >= 60) {
      return `for 1 t siden`;
    }
    return `for ${minutes} min siden`;
  } else if (currentHour >= 18 && currentHour < 23) {
    // Evening (peak time): purchases from 5-30 min ago
    const minutes = Math.floor(Math.random() * 25) + 5;
    return `for ${minutes} min siden`;
  } else {
    // Night (23:00 - 07:00): purchases from 1-3 hours ago
    const hours = Math.floor(Math.random() * 2) + 1;
    return `for ${hours} t siden`;
  }
}

// Progressive delay intervals (in ms)
// First popup comes quickly, then intervals increase
const DELAY_INTERVALS = [
  { min: 3000, max: 5000 },    // 1st: 3-5 seconds
  { min: 25000, max: 40000 },  // 2nd: 25-40 seconds
  { min: 50000, max: 70000 },  // 3rd: 50-70 seconds
  { min: 70000, max: 100000 }, // 4th: 70-100 seconds
];

function getDelayForShow(showIndex) {
  const interval = DELAY_INTERVALS[showIndex] || DELAY_INTERVALS[DELAY_INTERVALS.length - 1];
  return Math.floor(Math.random() * (interval.max - interval.min)) + interval.min;
}

export default function SocialProofPopup({ disabled = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showCount, setShowCount] = useState(0);
  const [timerId, setTimerId] = useState(null);
  const maxShows = 4;

  // Pages where popup should NOT show
  const isDisabledPage = useCallback(() => {
    const path = window.location.pathname;
    // Don't show during active quiz (but OK on intro and results)
    const isActiveQuiz = path === '/quiz' && document.querySelector('[data-testid="quiz-option-Fokus"]');
    return (
      isActiveQuiz ||
      path.includes('/checkout') ||
      path.includes('/handlekurv') ||
      disabled
    );
  }, [disabled]);

  const showNotification = useCallback(() => {
    if (isDisabledPage() || showCount >= maxShows) return;
    
    setNotification({
      product: getWeightedRandomProduct(),
      city: getRandomCity(),
      name: getRandomName(),
      timeAgo: getRealisticTimeAgo()
    });
    setIsVisible(true);
    setShowCount(prev => prev + 1);

    // Auto-hide after 7 seconds
    setTimeout(() => {
      setIsVisible(false);
    }, 7000);
  }, [isDisabledPage, showCount]);

  // Schedule next notification
  const scheduleNext = useCallback((nextShowIndex) => {
    if (nextShowIndex >= maxShows) return;
    
    const delay = getDelayForShow(nextShowIndex);
    const id = setTimeout(() => {
      if (!isDisabledPage()) {
        showNotification();
      }
    }, delay);
    setTimerId(id);
  }, [isDisabledPage, showNotification]);

  // Initial setup - show first popup quickly
  useEffect(() => {
    if (disabled) return;
    
    // First popup after 3-5 seconds
    const initialDelay = getDelayForShow(0);
    const id = setTimeout(() => {
      if (!isDisabledPage()) {
        showNotification();
      }
    }, initialDelay);
    setTimerId(id);

    return () => {
      if (id) clearTimeout(id);
    };
  }, [disabled]);

  // Schedule next popup after each show
  useEffect(() => {
    if (showCount > 0 && showCount < maxShows && !isVisible) {
      // Small delay after popup hides before scheduling next
      const scheduleDelay = setTimeout(() => {
        scheduleNext(showCount);
      }, 1000);
      
      return () => clearTimeout(scheduleDelay);
    }
  }, [showCount, isVisible, scheduleNext]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [timerId]);

  const handleClose = (e) => {
    e.stopPropagation();
    setIsVisible(false);
  };

  const handleClick = () => {
    window.open('https://www.nudgify.com/', '_blank', 'noopener,noreferrer');
  };

  if (!isVisible || !notification) return null;

  return (
    <div 
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-6 md:right-auto md:max-w-sm bg-white rounded-xl shadow-lg border border-neutral-100 p-4 z-50 cursor-pointer animate-slide-up"
      onClick={handleClick}
      data-testid="social-proof-popup"
    >
      {/* Close button */}
      <button 
        onClick={handleClose}
        className="absolute top-2 right-2 p-1.5 text-neutral-300 hover:text-neutral-500 transition-colors rounded-full hover:bg-neutral-50"
        aria-label="Lukk"
        data-testid="social-proof-close"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex gap-4">
        {/* Product Image */}
        <div className="w-16 h-16 bg-neutral-50 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
          <img 
            src={notification.product.image} 
            alt={notification.product.shortName}
            className="w-12 h-12 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-4">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <ThumbsUp className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-medium text-green-600">Populært</span>
            <span className="text-xs text-neutral-400 ml-auto">{notification.timeAgo}</span>
          </div>

          {/* Message with name */}
          <p className="text-sm text-neutral-700 leading-snug">
            <span className="font-semibold text-neutral-900">{notification.name}</span> fra {notification.city} kjøpte:
          </p>
          <p className="text-sm font-medium text-neutral-900 truncate mt-0.5">
            {notification.product.name}
          </p>

          {/* Nudgify Attribution */}
          <p className="text-[10px] text-neutral-400 mt-2">
            Verifisert av Nudgify
          </p>
        </div>
      </div>
    </div>
  );
}
